import { DateRange } from '../models/CustomerStats';

export const SALES_DAY_TIME_ZONE = 'Africa/Casablanca';
const SALES_DAY_START_HOUR = 6;

type ZonedDateParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

const formatter = new Intl.DateTimeFormat('en-CA-u-hc-h23', {
  timeZone: SALES_DAY_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

function zonedParts(date: Date): ZonedDateParts {
  const values = new Map(formatter.formatToParts(date).map((part) => [part.type, part.value]));
  return {
    year: Number(values.get('year')),
    month: Number(values.get('month')),
    day: Number(values.get('day')),
    hour: Number(values.get('hour')),
    minute: Number(values.get('minute')),
    second: Number(values.get('second')),
  };
}

function shiftDate(parts: Pick<ZonedDateParts, 'year' | 'month' | 'day'>, days: number) {
  const shifted = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + days));
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
  };
}

function timeZoneOffsetMs(date: Date): number {
  const parts = zonedParts(date);
  return Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
  ) - date.getTime();
}

function localSixToInstant(parts: Pick<ZonedDateParts, 'year' | 'month' | 'day'>): Date {
  const localAsUtc = Date.UTC(parts.year, parts.month - 1, parts.day, SALES_DAY_START_HOUR);
  let candidate = localAsUtc;
  // Re-evaluate the offset at the target instant so Morocco's DST/Ramadan
  // transitions do not shift the 06:00 business boundary.
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const adjusted = localAsUtc - timeZoneOffsetMs(new Date(candidate));
    if (adjusted === candidate) break;
    candidate = adjusted;
  }
  return new Date(candidate);
}

export function resolveSalesDayRange(now: Date = new Date()): DateRange {
  const localNow = zonedParts(now);
  const activeDate = shiftDate(
    localNow,
    localNow.hour < SALES_DAY_START_HOUR ? -1 : 0,
  );
  const nextDate = shiftDate(activeDate, 1);
  const start = localSixToInstant(activeDate);
  const end = new Date(localSixToInstant(nextDate).getTime() - 1);
  return { start, end };
}

export function matchesSalesDayRange(range: DateRange): boolean {
  if (range.end.getTime() < range.start.getTime()) return false;
  const start = zonedParts(range.start);
  const endExclusive = zonedParts(new Date(range.end.getTime() + 1));
  const expectedEndDate = shiftDate(start, 1);
  return start.hour === SALES_DAY_START_HOUR
    && start.minute === 0
    && start.second === 0
    && range.start.getMilliseconds() === 0
    && endExclusive.hour === SALES_DAY_START_HOUR
    && endExclusive.minute === 0
    && endExclusive.second === 0
    && new Date(range.end.getTime() + 1).getMilliseconds() === 0
    && endExclusive.year === expectedEndDate.year
    && endExclusive.month === expectedEndDate.month
    && endExclusive.day === expectedEndDate.day;
}

export function formatSalesDayDescription(range: DateRange): string {
  const dateFormatter = new Intl.DateTimeFormat('fr-FR', {
    timeZone: SALES_DAY_TIME_ZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const endExclusive = new Date(range.end.getTime() + 1);
  return `Journée de vente · ${dateFormatter.format(range.start)} 06:00 → ${dateFormatter.format(endExclusive)} 06:00`;
}
